import React, { useCallback, useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import {
  FaCamera,
  FaTrash,
  FaUpload,
  FaVideo,
  FaExternalLinkAlt,
  FaIdBadge,
  FaCheckCircle,
  FaTimesCircle,
  FaRegClock,
  FaShieldAlt,
  FaPaperPlane,
  FaMapMarkerAlt,
} from "react-icons/fa";
import api from "../../../libs/axios";
import { uploadFilesWithPresignedPayload } from "../../../libs/presigned-upload";
import { useConfirm } from "../../../provider/confirm.provider";
import { getBrowserLocation } from "../../../utils/geolocation.utils";

/**
 * Check-in media for one visit, and the approval of it.
 *
 * Two different things, submitted by the engineer before checking in:
 *
 *   Visit verification   ONE image — the engineer on this site, today. There is
 *                        only ever one, so submitting another REPLACES it.
 *   Safety verification  As many photos and videos as it took to record the
 *                        site's safety condition.
 *
 * Both are stored as ordinary visit attachments under dedicated document types,
 * which is why they have their own endpoint and their own panel instead of being
 * mixed into "Visit files" — the work photos uploaded later in the day are a
 * different thing entirely.
 *
 * On a project that does not allow check-in without verification, each of the
 * two is also a PART of the visit's verification step: someone holding
 * IncSite.visit_verification_approve approves or rejects it, and the engineer
 * cannot check in until both are approved. An approval is a decision about the
 * step, not the files — a part can be approved with nothing uploaded. That is
 * why the media can now be uploaded BEFORE check-in: it has to be seen first.
 *
 * The engineer has ONE action here: submit. It sends the file URLs and the
 * browser's location in a single call (POST /inc/visits/:id/verification); the
 * API saves the files and, before check-in, that same call is the request for
 * review - on every project. Where the project requires approval, check-in
 * waits for it; elsewhere the engineer may check in while it is reviewed. Check-in afterwards is location and address only. The
 * location is asked for BEFORE anything is sent to S3, and each file then shows
 * where it was submitted from and how far that is from the site — which is
 * what a reviewer is judging.
 *
 * Submitting and deleting stay with the visit's own engineer; the media itself
 * is visible to anyone who can see the visit.
 */

export const VERIFICATION_FOLDER = "inc/visit-verification";
// The camera-facing input accepts both, so the video/photo split is decided by
// what the user actually picked, not by two separate buttons.
export const SAFETY_ACCEPT = "image/*,video/*";
// The visit verification image is a photo by definition — the API refuses a clip.
export const VISIT_VERIFICATION_ACCEPT = "image/*";

export type VerificationPart = "visit" | "safety";

export interface VerificationPartState {
  status: "pending" | "approved" | "rejected" | null;
  remarks: string | null;
  reviewed_by: number | null;
  reviewed_at: string | null;
  reviewer: { id: number; first_name: string; last_name: string } | null;
}

export interface VisitVerification {
  required: boolean;
  status: "not_required" | "approved" | "rejected" | "pending";
  can_check_in: boolean;
  blocked_reason: string | null;
  visit: VerificationPartState;
  safety: VerificationPartState;
}

interface VerificationMediaPanelProps {
  visitId: number;
  visitNumber: number;
  // True only for the visit's own engineer, with the visit_files permission, on
  // a site that is not locked. Gates DELETING a file — mirrors the API.
  canManage: boolean;
  checkedIn: boolean;
  // planned | in_progress | completed | cancelled
  visitStatus: string;
  // The visit's own engineer. They upload and may ask for a review, but never
  // review their own visit.
  isOwner: boolean;
  // Holds IncSite.visit_verification_approve on a site that is not locked.
  canReview: boolean;
  // Holds IncSite.visit_check_in on a site that is not locked — submitting the
  // verification is part of checking in, so it shares that permission.
  canSubmit: boolean;
  // Tells the visit card the verification state moved, so its Check In button
  // and badge follow without the whole tab reloading by hand.
  onVerificationChange?: () => void;
}

const formatSize = (bytes?: number) => {
  const size = Number(bytes || 0);
  if (!size) return "";
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(0)} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
};

const stamp = (v?: string | null) => {
  if (!v) return "";
  const d = new Date(v);
  return isNaN(d.getTime())
    ? ""
    : d.toLocaleString(undefined, {
        day: "2-digit",
        month: "short",
        hour: "2-digit",
        minute: "2-digit",
      });
};

const uploaderName = (f: any) =>
  f?.uploader ? `${f.uploader.first_name || ""} ${f.uploader.last_name || ""}`.trim() : "";

const distanceText = (meters: number) =>
  meters >= 1000 ? `${(meters / 1000).toFixed(meters >= 10000 ? 0 : 1)} km` : `${Math.round(meters)} m`;

// Where one file was captured: a link out to the map and the distance from the
// site, red when that is outside the geo-fence. Media uploaded before the
// location was recorded says so rather than showing nothing.
const CaptureLocation: React.FC<{ file: any; className?: string }> = ({ file, className = "" }) => {
  const lat = Number(file?.latitude);
  const lng = Number(file?.longitude);
  if (file?.latitude == null || file?.longitude == null || !Number.isFinite(lat) || !Number.isFinite(lng)) {
    return <span className={`text-[10px] text-gray-400 ${className}`}>Location not recorded</span>;
  }
  const distance = file.distance_meters == null ? null : Number(file.distance_meters);
  const outside = file.within_geofence === false;
  return (
    <a
      href={`https://www.google.com/maps?q=${lat},${lng}`}
      target="_blank"
      rel="noreferrer"
      title={`Captured at ${lat.toFixed(6)}, ${lng.toFixed(6)} — open in Google Maps`}
      className={`inline-flex items-center gap-1 text-[10px] hover:underline ${outside ? "text-red-600" : "text-blue-700"} ${className}`}
    >
      <FaMapMarkerAlt className="w-2.5 h-2.5 flex-shrink-0" />
      <span className="truncate">
        {distance != null && Number.isFinite(distance)
          ? `${distanceText(distance)} from site`
          : `${lat.toFixed(5)}, ${lng.toFixed(5)}`}
      </span>
    </a>
  );
};

const PART_LABEL: Record<VerificationPart, string> = {
  visit: "Visit verification",
  safety: "Safety verification",
};

// One part's approval state, as a pill.
export const VerificationStatusBadge: React.FC<{ status: VerificationPartState["status"] | VisitVerification["status"] }> = ({
  status,
}) => {
  const tone =
    status === "approved"
      ? "bg-green-100 text-green-800"
      : status === "rejected"
        ? "bg-red-100 text-red-800"
        : status === "pending"
          ? "bg-amber-100 text-amber-800"
          : "bg-gray-100 text-gray-600";
  const Icon = status === "approved" ? FaCheckCircle : status === "rejected" ? FaTimesCircle : FaRegClock;
  const label =
    status === "approved"
      ? "Approved"
      : status === "rejected"
        ? "Rejected"
        : status === "pending"
          ? "Awaiting approval"
          : status === "not_required"
            ? "Not required"
            : "Not submitted";
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-medium rounded-full ${tone}`}>
      <Icon className="w-2.5 h-2.5" /> {label}
    </span>
  );
};

const VerificationMediaPanel: React.FC<VerificationMediaPanelProps> = ({
  visitId,
  visitNumber,
  canManage,
  checkedIn,
  visitStatus,
  isOwner,
  canReview,
  canSubmit,
  onVerificationChange,
}) => {
  const { confirm, prompt } = useConfirm();
  const [visitVerification, setVisitVerification] = useState<any>(null);
  const [photos, setPhotos] = useState<any[]>([]);
  const [videos, setVideos] = useState<any[]>([]);
  const [verification, setVerification] = useState<VisitVerification | null>(null);
  const [loading, setLoading] = useState(true);
  // What the engineer has picked but not submitted yet: the one visit
  // verification photo, and the safety photos / videos.
  const [visitPick, setVisitPick] = useState<File | null>(null);
  const [selected, setSelected] = useState<File[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [progress, setProgress] = useState(0);
  const [reviewing, setReviewing] = useState(false);
  // Waiting on the browser's location fix.
  const [locating, setLocating] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const visitInputRef = useRef<HTMLInputElement>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/inc/visits/${visitId}/verification-files`);
      setVisitVerification(res.data?.data?.visit_verification || null);
      setPhotos(res.data?.data?.photos || []);
      setVideos(res.data?.data?.videos || []);
      setVerification(res.data?.data?.verification || null);
    } catch {
      /* non-fatal — the panel just shows nothing */
    } finally {
      setLoading(false);
    }
  }, [visitId]);

  useEffect(() => {
    load();
  }, [load]);

  // Anything that can move the approval state also refreshes the visit card.
  const reloadAll = () => {
    load();
    onVerificationChange?.();
  };

  const isPlanned = visitStatus === "planned";
  const isOpenVisit = visitStatus === "planned" || visitStatus === "in_progress";
  const required = !!verification?.required;
  // The engineer's one action: submit the verification. Open visits only —
  // before check-in it is what gets approved, after it a late photo or clip can
  // still be added.
  const canSubmitThis = canSubmit && isOwner && isOpenVisit;
  // Before check-in a submission is a request for review on every project. The
  // project's switch (`required`) only decides whether check-in waits for it.
  const requesting = isPlanned;
  // Nobody reviews their own visit; a closed visit has nothing left to review.
  const canReviewThis = canReview && !isOwner && isOpenVisit;

  const pickVisitPhoto = (picked: FileList | null) => {
    const file = picked?.[0];
    if (!file) return;
    if (file.type.startsWith("video/")) {
      if (visitInputRef.current) visitInputRef.current.value = "";
      return toast.error("The visit verification must be a photo. Add clips under safety photos & videos.");
    }
    setVisitPick(file);
  };

  const resetPicks = () => {
    setVisitPick(null);
    setSelected([]);
    if (visitInputRef.current) visitInputRef.current.value = "";
    if (inputRef.current) inputRef.current.value = "";
  };

  // ONE call: the files that were picked plus where the browser is. The API
  // saves them against the visit and, where approval is required, that same
  // call is the request for it — there is no separate "request approval" step.
  const submit = async () => {
    const hasFiles = Boolean(visitPick) || selected.length > 0;
    if (!hasFiles && !requesting) {
      return toast.error("Choose the visit verification photo or at least one safety photo or video");
    }
    if (!hasFiles) {
      const ok = await confirm({
        title: "Request approval without files?",
        message:
          "Nothing is selected. The approvers are asked to approve this visit's verification as it stands — they can approve it with or without files.",
        confirmText: "Request approval",
        variant: "warning",
      });
      if (!ok) return;
    }
    if (visitPick && visitVerification) {
      const ok = await confirm({
        title: "Replace the visit verification photo?",
        message:
          requesting && verification?.visit.status === "approved"
            ? "A visit carries one verification photo. The one already on this visit will be deleted, and because it was already approved the visit verification goes back to awaiting approval."
            : "A visit carries one verification photo. The one already on this visit will be deleted.",
        confirmText: "Replace photo",
        variant: "warning",
      });
      if (!ok) return;
    }

    // Location first: the API refuses a submission without it, and asking now
    // means a denied permission costs nothing — no file has gone to S3 yet.
    setLocating(true);
    let where;
    try {
      where = await getBrowserLocation("submitted");
    } catch (error: any) {
      setLocating(false);
      return toast.error(error?.message || "A location fix is required to submit verification");
    }
    setLocating(false);

    setSubmitting(true);
    setProgress(0);
    try {
      // Both kinds go up in one batch so the progress bar means something, then
      // they are split apart again: the visit photo, when there is one, is first.
      const uploaded = hasFiles
        ? await uploadFilesWithPresignedPayload(
            [...(visitPick ? [{ file: visitPick }] : []), ...selected.map((file) => ({ file }))],
            { folderName: VERIFICATION_FOLDER, onProgress: (p) => setProgress(p.percentage) },
          )
        : [];
      const res = await api.post(`/inc/visits/${visitId}/verification`, {
        latitude: where.latitude,
        longitude: where.longitude,
        visit_verification_file: visitPick ? uploaded[0] : undefined,
        safety_files: visitPick ? uploaded.slice(1) : uploaded,
      });
      if (res.data?.data?.within_geofence === false) {
        toast(`Submitted from outside the ${res.data?.data?.geo_fence_meters || 500}m geo-fence`, { icon: "⚠️" });
      }
      resetPicks();
      reloadAll();
    } catch (error: any) {
      // A presigned upload failure is not surfaced by the axios interceptor.
      if (!error?.response) toast.error(error?.message || "Upload failed — nothing was submitted");
    } finally {
      setSubmitting(false);
      setProgress(0);
    }
  };

  const remove = async (file: any, kind: "visit verification photo" | "safety verification file") => {
    const ok = await confirm({
      title: `Remove ${kind}`,
      message: `Remove "${file.file_name || `#${file.id}`}"? This is check-in proof for the visit and cannot be recovered.`,
      confirmText: "Remove file",
      variant: "danger",
    });
    if (!ok) return;
    try {
      await api.delete(`/inc/verification-files/${file.id}`);
      reloadAll();
    } catch {
      /* the axios interceptor already surfaced the message */
    }
  };

  // Approve or reject one part, or both at once ("all").
  const review = async (part: VerificationPart | "all", action: "approve" | "reject") => {
    const label = part === "all" ? "Visit and safety verification" : PART_LABEL[part];
    let remarks: string | null = "";
    if (action === "reject") {
      remarks = await prompt({
        title: `Reject ${label.toLowerCase()} — visit #${visitNumber}`,
        message: "The engineer is shown these remarks and has to correct this before they can check in.",
        label: "Reason for rejecting",
        placeholder: "What is wrong or missing?",
        required: true,
        multiline: true,
        maxLength: 2000,
        confirmText: "Reject",
        variant: "danger",
      });
      if (remarks === null) return;
      if (!remarks.trim()) return toast.error("Remarks are required to reject");
    } else {
      const fileCount = (visitVerification ? 1 : 0) + photos.length + videos.length;
      const ok = await confirm({
        title: `Approve ${label.toLowerCase()} — visit #${visitNumber}?`,
        message:
          part === "all"
            ? `Both parts of the verification step are approved${fileCount === 0 ? " — there are no files on this visit, and that is allowed" : ""}. The engineer can then check in.`
            : `${label} is approved, whether or not any file was uploaded for it. Check-in opens once both parts are approved.`,
        confirmText: "Approve",
        variant: "info",
      });
      if (!ok) return;
    }
    setReviewing(true);
    try {
      await api.post(`/inc/visits/${visitId}/verification/review`, {
        part,
        action,
        remarks: remarks?.trim() || undefined,
      });
      reloadAll();
    } catch {
      /* the axios interceptor already surfaced the message */
    } finally {
      setReviewing(false);
    }
  };

  const safetyTotal = photos.length + videos.length;
  const hasMedia: Record<VerificationPart, boolean> = { visit: Boolean(visitVerification), safety: safetyTotal > 0 };
  // Something to review: the project requires the step, a part already carries
  // a decision or a request, or media is on the visit (including media sent
  // before submissions were put up for review on every project).
  const reviewable =
    !!verification &&
    (required || verification.status !== "not_required" || hasMedia.visit || hasMedia.safety);

  // Status line + Approve / Reject for one part. Only rendered on a project
  // that requires the step, or when a part already carries a decision.
  const partHeader = (part: VerificationPart) => {
    const state = verification?.[part];
    if (!verification || (!required && !state?.status && !hasMedia[part])) return null;
    const reviewer = state?.reviewer ? `${state.reviewer.first_name || ""} ${state.reviewer.last_name || ""}`.trim() : "";
    return (
      <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
        <div className="flex flex-wrap items-center gap-2 text-[11px] text-gray-500">
          <VerificationStatusBadge status={state?.status ?? null} />
          {state?.reviewed_at && (
            <span>
              {stamp(state.reviewed_at)}
              {reviewer ? ` · ${reviewer}` : ""}
            </span>
          )}
          {state?.remarks && <span className="text-gray-700 break-words">“{state.remarks}”</span>}
        </div>
        {canReviewThis && (
          <div className="flex items-center gap-1.5">
            {state?.status !== "approved" && (
              <button
                onClick={() => review(part, "approve")}
                disabled={reviewing}
                className="inline-flex items-center gap-1 px-2.5 py-1 text-xs text-white bg-green-700 rounded hover:bg-green-800 disabled:opacity-60"
              >
                <FaCheckCircle className="w-3 h-3" /> Approve
              </button>
            )}
            {state?.status !== "rejected" && (
              <button
                onClick={() => review(part, "reject")}
                disabled={reviewing}
                className="inline-flex items-center gap-1 px-2.5 py-1 text-xs text-red-700 border border-red-200 bg-red-50 rounded hover:bg-red-100 disabled:opacity-60"
              >
                <FaTimesCircle className="w-3 h-3" /> Reject
              </button>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-4">
      {/* ── The verification step as a whole ──────────────────────────────── */}
      {reviewable && verification && (
        <div
          className={`rounded border px-3 py-2 ${
            verification.status === "approved"
              ? "border-green-200 bg-green-50"
              : verification.status === "rejected"
                ? "border-red-200 bg-red-50"
                : "border-amber-200 bg-amber-50"
          }`}
        >
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-start gap-2 text-sm text-gray-800 min-w-0">
              <FaShieldAlt className="w-4 h-4 mt-0.5 flex-shrink-0 text-gray-500" />
              <div>
                <div className="font-medium flex flex-wrap items-center gap-2">
                  {required ? "Verification before check-in" : "Check-in verification"}{" "}
                  <VerificationStatusBadge
                    status={
                      !required && verification.status === "not_required"
                        ? verification.visit.status ?? verification.safety.status ?? null
                        : verification.status
                    }
                  />
                </div>
                <p className="text-xs text-gray-600 mt-0.5">
                  {checkedIn
                    ? "This visit is already checked in."
                    : verification.status === "approved"
                      ? "Both parts are approved — the engineer can check in."
                      : required
                        ? "This project requires the visit verification and the safety verification to be approved before check-in. Each can be approved with or without files."
                        : "Review the visit verification and the safety verification. This project lets the engineer check in without waiting for the approval."}
                </p>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-1.5">
              {canReviewThis && verification.status !== "approved" && (
                <button
                  onClick={() => review("all", "approve")}
                  disabled={reviewing}
                  className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs text-white bg-green-700 rounded hover:bg-green-800 disabled:opacity-60"
                >
                  <FaCheckCircle className="w-3 h-3" /> Approve both
                </button>
              )}
            </div>
          </div>
          {canReview && isOwner && isOpenVisit && verification.status !== "approved" && (
            <p className="text-[11px] text-gray-500 mt-1">You created this visit, so its verification has to be reviewed by someone else.</p>
          )}
        </div>
      )}

      {/* ── Submit — the engineer's one action ───────────────────────────── */}
      {canSubmitThis && (
        <div className="border border-blue-200 bg-blue-50/40 rounded p-3 space-y-3">
          <div>
            <div className="text-sm font-medium text-gray-800 flex items-center gap-2">
              <FaPaperPlane className="w-3 h-3 text-blue-800" />
              {requesting ? "Submit verification for approval" : "Add verification photos & videos"}
            </div>
            <p className="text-[11px] text-gray-500 mt-0.5">
              {requesting
                ? required
                  ? "Pick the photos, then submit once. Your location is recorded with them and the approvers are notified — check-in opens when both parts are approved."
                  : "Pick the photos, then submit once. Your location is recorded with them and the approvers are notified. This project does not make check-in wait for the approval."
                : "Pick the photos, then submit once. Your location is recorded with them."}
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Visit verification photo <span className="text-gray-400 font-normal">(one)</span>
              </label>
              <input
                ref={visitInputRef}
                type="file"
                accept={VISIT_VERIFICATION_ACCEPT}
                // On a phone this opens the camera directly; on a desktop browser it
                // is ignored and the normal file picker appears.
                capture="environment"
                disabled={submitting || locating}
                onChange={(e) => pickVisitPhoto(e.target.files)}
                className="text-xs"
              />
              {visitPick && visitVerification && (
                <p className="text-[11px] text-gray-400 mt-1">Replaces the photo already on this visit.</p>
              )}
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Safety photos &amp; videos <span className="text-gray-400 font-normal">(any number)</span>
              </label>
              <input
                ref={inputRef}
                type="file"
                multiple
                accept={SAFETY_ACCEPT}
                capture="environment"
                disabled={submitting || locating}
                onChange={(e) => setSelected(Array.from(e.target.files || []))}
                className="text-xs"
              />
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button
              onClick={submit}
              disabled={submitting || locating || (!requesting && !visitPick && selected.length === 0)}
              className="inline-flex items-center gap-2 px-3 py-1.5 text-sm text-white bg-blue-800 rounded hover:bg-blue-900 disabled:opacity-60"
            >
              <FaUpload className="w-3 h-3" />
              {locating
                ? "Getting your location…"
                : submitting
                  ? `Uploading ${progress}%`
                  : requesting
                    ? visitPick || selected.length
                      ? `Submit for approval (${(visitPick ? 1 : 0) + selected.length})`
                      : "Request approval without files"
                    : `Submit${visitPick || selected.length ? ` (${(visitPick ? 1 : 0) + selected.length})` : ""}`}
            </button>
            {(visitPick || selected.length > 0) && !submitting && !locating && (
              <button onClick={resetPicks} className="text-xs text-gray-500 hover:underline">
                Clear selection
              </button>
            )}
          </div>
        </div>
      )}

      {/* ── Visit verification — the single image ───────────────────────── */}
      <div>
        <div className="text-xs font-semibold text-gray-600 mb-1 flex items-center gap-1">
          <FaIdBadge className="w-3 h-3 text-blue-700" /> Visit Verification
        </div>
        <p className="text-[11px] text-gray-400 mb-2">
          One photo of the engineer on site{required ? ", submitted before check-in so it can be approved." : "."}
        </p>
        {partHeader("visit")}

        {loading ? (
          <p className="text-xs text-gray-400 mb-2">Loading…</p>
        ) : visitVerification ? (
          <div className="flex items-start gap-3 border border-gray-200 rounded p-2 max-w-md">
            {visitVerification.url ? (
              <a
                href={visitVerification.url}
                target="_blank"
                rel="noreferrer"
                title={visitVerification.file_name || ""}
                className="flex-shrink-0"
              >
                <img
                  src={visitVerification.url}
                  alt={visitVerification.file_name || "Visit verification"}
                  loading="lazy"
                  className="w-24 h-24 object-cover rounded"
                />
              </a>
            ) : (
              // A key that no longer resolves in S3 still gets a tile, so a
              // missing file is visible rather than silently absent.
              <div className="w-24 h-24 flex items-center justify-center text-[10px] text-gray-400 bg-gray-50 rounded flex-shrink-0 text-center px-1">
                Preview unavailable
              </div>
            )}
            <div className="min-w-0 flex-1">
              <div className="text-xs text-gray-700 truncate">{visitVerification.file_name || `#${visitVerification.id}`}</div>
              <div className="text-[10px] text-gray-400">
                {formatSize(visitVerification.file_size)}
                {stamp(visitVerification.uploaded_at) ? ` · ${stamp(visitVerification.uploaded_at)}` : ""}
                {uploaderName(visitVerification) ? ` · ${uploaderName(visitVerification)}` : ""}
              </div>
              <CaptureLocation file={visitVerification} className="mt-0.5" />
            </div>
            {canManage && (
              <button
                onClick={() => remove(visitVerification, "visit verification photo")}
                title="Remove"
                className="text-red-500 flex-shrink-0"
              >
                <FaTrash className="w-3 h-3" />
              </button>
            )}
          </div>
        ) : (
          <p className="text-xs text-gray-400 mb-2">No visit verification photo on this visit.</p>
        )}
      </div>

      {/* ── Safety verification — photos and videos ─────────────────────── */}
      <div>
        <div className="text-xs font-semibold text-gray-600 mb-1 flex items-center gap-1">
          <FaCamera className="w-3 h-3 text-emerald-600" /> Safety Verification Photos &amp; Videos
          {safetyTotal > 0 && <span className="text-gray-400 font-normal">({safetyTotal})</span>}
        </div>
        <p className="text-[11px] text-gray-400 mb-2">
          The site's safety condition{required ? ", submitted before check-in so it can be approved." : "."}
        </p>
        {partHeader("safety")}

        {loading ? (
          <p className="text-xs text-gray-400 mb-2">Loading…</p>
        ) : safetyTotal === 0 ? (
          <p className="text-xs text-gray-400 mb-2">No safety verification photos or videos on this visit.</p>
        ) : (
          <div className="space-y-3 mb-3">
            {photos.length > 0 && (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2">
                {photos.map((f) => (
                  <div key={f.id} className="relative group border border-gray-200 rounded overflow-hidden bg-gray-50">
                    {f.url ? (
                      <a href={f.url} target="_blank" rel="noreferrer" title={f.file_name || ""}>
                        <img
                          src={f.url}
                          alt={f.file_name || "Safety verification photo"}
                          loading="lazy"
                          className="w-full h-24 object-cover"
                        />
                      </a>
                    ) : (
                      <div className="w-full h-24 flex items-center justify-center text-[10px] text-gray-400 px-1 text-center">
                        Preview unavailable
                      </div>
                    )}
                    <div className="px-1.5 py-1 text-[10px] text-gray-500">
                      <div className="truncate">
                        {stamp(f.uploaded_at)}
                        {uploaderName(f) ? ` · ${uploaderName(f)}` : ""}
                      </div>
                      <CaptureLocation file={f} className="max-w-full" />
                    </div>
                    {canManage && (
                      <button
                        onClick={() => remove(f, "safety verification file")}
                        title="Remove"
                        className="absolute top-1 right-1 p-1 rounded bg-white/90 text-red-600 opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity"
                      >
                        <FaTrash className="w-2.5 h-2.5" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}

            {videos.length > 0 && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                {videos.map((f) => (
                  <div key={f.id} className="border border-gray-200 rounded overflow-hidden bg-gray-50">
                    {f.url ? (
                      <video src={f.url} controls preload="metadata" className="w-full max-h-48 bg-black" />
                    ) : (
                      <div className="h-24 flex items-center justify-center text-[10px] text-gray-400">
                        Preview unavailable
                      </div>
                    )}
                    <div className="px-2 py-1 flex items-center justify-between gap-2">
                      <div className="min-w-0">
                        <div className="text-[11px] text-gray-700 truncate flex items-center gap-1">
                          <FaVideo className="w-2.5 h-2.5 text-gray-400 flex-shrink-0" />
                          {f.url ? (
                            <a href={f.url} target="_blank" rel="noreferrer" className="text-blue-700 hover:underline truncate">
                              {f.file_name || `#${f.id}`}
                            </a>
                          ) : (
                            <span className="truncate">{f.file_name || `#${f.id}`}</span>
                          )}
                          {f.url && <FaExternalLinkAlt className="w-2 h-2 text-gray-400 flex-shrink-0" />}
                        </div>
                        <div className="text-[10px] text-gray-400 truncate">
                          {formatSize(f.file_size)}
                          {stamp(f.uploaded_at) ? ` · ${stamp(f.uploaded_at)}` : ""}
                          {uploaderName(f) ? ` · ${uploaderName(f)}` : ""}
                        </div>
                        <CaptureLocation file={f} className="max-w-full" />
                      </div>
                      {canManage && (
                        <button
                          onClick={() => remove(f, "safety verification file")}
                          title="Remove"
                          className="text-red-500 flex-shrink-0"
                        >
                          <FaTrash className="w-3 h-3" />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default VerificationMediaPanel;
